import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/AIMachineLearning.css';

const AIMachineLearning = () => {
  const { openContactModal } = useModal();

  return (
    <div className="ai-ml-page">
      {/* Hero Section */}
      <section className="ai-ml-hero">
        <div className="ai-ml-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="ai-ml-hero-content"
          >
            <div className="hero-badge">
              🤖 AI-Powered Solutions
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Advanced <span class=\"gradient-text\">AI & Machine Learning</span><br />Solutions" }}
            />
            
            <p className="hero-subtitle">
              Leverage cutting-edge AI and machine learning technologies to transform your business operations, gain insights from data, and automate complex processes.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'AI & Machine Learning Services',
                  'AI Machine Learning Page - Get Started Today'
                )}
              >
                Get Started Today
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'AI & Machine Learning Information',
                  'AI Machine Learning Page - Learn More'
                )}
              >
                Learn More
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="ai-ml-section">
        <div className="ai-ml-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="ai-ml-section-title gradient-text">AI & Machine Learning Services</h2>
            <p className="ai-ml-section-subtitle">
              Comprehensive AI solutions tailored to transform your business operations and unlock data-driven insights
            </p>
            
            <div className="ai-ml-services-grid">
              
              <div className="ai-ml-glass-card">
                <h3>Machine Learning Models</h3>
                <p>Custom ML models for prediction, classification, and pattern recognition tailored to your data.</p>
                <ul>
                  <li>✓ Predictive Analytics & Forecasting</li>
                  <li>✓ Classification & Clustering Models</li>
                  <li>✓ Recommendation Systems</li>
                  <li>✓ Time Series Analysis</li>
                </ul>
              </div>
              
              <div className="ai-ml-glass-card">
                <h3>Natural Language Processing</h3>
                <p>Advanced NLP solutions for text analysis, sentiment analysis, and language understanding.</p>
                <ul>
                  <li>✓ Text Analytics & Mining</li>
                  <li>✓ Sentiment Analysis</li>
                  <li>✓ Intelligent Chatbots</li>
                  <li>✓ Language Translation</li>
                </ul>
              </div>
              
              <div className="ai-ml-glass-card">
                <h3>Computer Vision</h3>
                <p>Image and video analysis solutions for object detection, recognition, and visual automation.</p>
                <ul>
                  <li>✓ Object Detection & Recognition</li>
                  <li>✓ Image Classification</li>
                  <li>✓ Video Analytics</li>
                  <li>✓ Quality Inspection Systems</li>
                </ul>
              </div>
              
              <div className="ai-ml-glass-card">
                <h3>AI Integration</h3>
                <p>Seamless integration of AI capabilities into existing business processes and applications.</p>
                <ul>
                  <li>✓ API Integration & Development</li>
                  <li>✓ Workflow Automation</li>
                  <li>✓ Decision Support Systems</li>
                  <li>✓ Process Optimization</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="ai-ml-section">
        <div className="ai-ml-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="ai-ml-section-title gradient-text">Why Choose Our AI Solutions</h2>
            <p className="ai-ml-section-subtitle">
              Experience the transformative power of artificial intelligence for your business
            </p>
            
            <div className="ai-ml-benefits-grid">
              
              <div className="ai-ml-benefit-card">
                <div className="ai-ml-benefit-icon">📊</div>
                <h4 className="ai-ml-benefit-title">Data-Driven Insights</h4>
                <p className="ai-ml-benefit-description">Extract valuable insights from your data to make informed decisions</p>
              </div>
              
              <div className="ai-ml-benefit-card">
                <div className="ai-ml-benefit-icon">⚡</div>
                <h4 className="ai-ml-benefit-title">Process Automation</h4>
                <p className="ai-ml-benefit-description">Automate repetitive tasks and improve operational efficiency</p>
              </div>
              
              <div className="ai-ml-benefit-card">
                <div className="ai-ml-benefit-icon">🎯</div>
                <h4 className="ai-ml-benefit-title">Improved Accuracy</h4>
                <p className="ai-ml-benefit-description">AI-powered solutions deliver higher accuracy and consistency</p>
              </div>
              
              <div className="ai-ml-benefit-card">
                <div className="ai-ml-benefit-icon">💡</div>
                <h4 className="ai-ml-benefit-title">Innovation Catalyst</h4>
                <p className="ai-ml-benefit-description">Drive innovation and competitive advantage through AI adoption</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ai-ml-section">
        <div className="ai-ml-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="ai-ml-cta-card">
              <h3 className="ai-ml-cta-title">
                Ready to Harness AI for Your Business?
              </h3>
              <p className="ai-ml-cta-subtitle">
                Contact us today to discover how our AI and machine learning services can transform your operations and drive intelligent automation.
              </p>
              <div className="ai-ml-cta-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'AI Journey Project',
                    'AI Machine Learning Page - Start AI Journey'
                  )}
                >
                  Start AI Journey
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'AI Consultation',
                    'AI Machine Learning Page - Schedule Consultation'
                  )}
                >
                  Schedule Consultation
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIMachineLearning;